import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Single-flight refresh: if multiple requests 401 at once, only one /auth/refresh
// is issued and all retries wait on it.
let _refreshInflight = null;

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const orig = error.config || {};
    const status = error?.response?.status;
    const url = orig.url || "";
    // Don't retry the auth endpoints themselves.
    const isAuthCall = url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/me");
    if (status === 401 && !orig._retry && !isAuthCall) {
      orig._retry = true;
      try {
        if (!_refreshInflight) {
          _refreshInflight = api.post("/auth/refresh").finally(() => { _refreshInflight = null; });
        }
        await _refreshInflight;
        return api(orig);
      } catch (_) {
        // Refresh failed — let caller handle (typically redirect to /login).
      }
    }
    // Normalise FastAPI validation-error payloads. FastAPI returns 422
    // with ``detail`` as an ARRAY of {type, loc, msg, input, url}
    // objects. Nearly every ``toast.error(..., { description:
    // e.response.data.detail })`` call across the app assumes a string
    // and will hand an array-of-objects straight to Sonner → React
    // error #31 ("Objects are not valid as a React child") → white
    // screen. Coerce here so callers never see the array shape.
    try {
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const summary = detail
          .map((d) => {
            if (typeof d === "string") return d;
            if (!d || typeof d !== "object") return String(d);
            const loc = Array.isArray(d.loc)
              ? d.loc.filter((s) => s !== "body").join(".")
              : "";
            const msg = d.msg || d.message || "invalid value";
            return loc ? `${loc}: ${msg}` : msg;
          })
          .filter(Boolean)
          .join("; ");
        error.response.data.detail = summary || "Request validation failed";
      } else if (detail && typeof detail === "object") {
        // Rare: some routes return an object under detail.
        error.response.data.detail = detail.msg || JSON.stringify(detail);
      }
    } catch (_) { /* never let the interceptor itself throw */ }
    return Promise.reject(error);
  }
);

export function downloadUrl(path, params = {}) {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  ).toString();
  return `${API}${path}${qs ? `?${qs}` : ""}`;
}
